import { createSlice } from '@reduxjs/toolkit'

import { IAuthState } from '@store/features/auth/auth.types'
import {
  loginThunk,
  initializeSession,
  logoutThunk,
  setPasswordThunk,
  resetPasswordThunk
} from '@store/features/auth/auth.actions'
import {
  hasStoredSession,
  mapProfileToAuthUser,
  syncTokensToAuthState
} from '@utils/auth-session'

const emptyAuthState = (): IAuthState => ({
  user: null,
  errors: null,
  accessToken: '',
  refreshToken: '',
  sessionStatus: 'anonymous',
  execution: {
    isLoading: false,
    status: false
  }
})

const createInitialState = (): IAuthState => ({
  ...emptyAuthState(),
  ...syncTokensToAuthState(),
  sessionStatus: hasStoredSession() ? 'unknown' : 'anonymous'
})

const applyAuthenticatedUser = (
  state: IAuthState,
  payload: { userData: Parameters<typeof mapProfileToAuthUser>[0] },
  tokens?: { accessToken: string; refreshToken: string }
) => {
  const user = mapProfileToAuthUser(payload.userData)

  if (!user) {
    state.sessionStatus = 'anonymous'
    state.user = null
    state.accessToken = ''
    state.refreshToken = ''
    return
  }

  state.sessionStatus = 'authenticated'
  state.user = user

  if (tokens) {
    state.accessToken = tokens.accessToken
    state.refreshToken = tokens.refreshToken
  } else {
    const synced = syncTokensToAuthState()
    state.accessToken = synced.accessToken
    state.refreshToken = synced.refreshToken
  }
}

const applyAnonymousSession = (state: IAuthState) => {
  state.sessionStatus = 'anonymous'
  state.user = null
  state.accessToken = ''
  state.refreshToken = ''
  state.execution.isLoading = false
}

const authSlice = createSlice({
  name: 'auth',
  initialState: createInitialState(),
  reducers: {
    executionLoading: (state, { payload }) => {
      state.execution.isLoading = payload.isLoading
    },
    resetExecutionState: (state) => {
      state.execution.status = false
      state.execution.isLoading = false
    },
    resetAuthState: () => emptyAuthState(),
    setSessionAnonymous: (state) => {
      if (state.sessionStatus === 'unknown') {
        state.sessionStatus = 'anonymous'
        state.execution.isLoading = false
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.execution.isLoading = true
      })
      .addCase(loginThunk.fulfilled, (state, { payload }) => {
        state.execution.isLoading = false
        applyAuthenticatedUser(state, payload, {
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken
        })
      })
      .addCase(loginThunk.rejected, (state) => {
        applyAnonymousSession(state)
      })
      .addCase(initializeSession.pending, (state) => {
        state.execution.isLoading = true
      })
      .addCase(initializeSession.fulfilled, (state, { payload }) => {
        state.execution.isLoading = false
        applyAuthenticatedUser(state, payload)
      })
      .addCase(initializeSession.rejected, (state) => {
        applyAnonymousSession(state)
      })
      .addCase(logoutThunk.pending, (state) => {
        state.execution.isLoading = true
      })
      .addCase(logoutThunk.fulfilled, () => emptyAuthState())
      .addCase(logoutThunk.rejected, () => emptyAuthState())
      .addCase(resetPasswordThunk.pending, (state) => {
        state.execution.isLoading = true
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.execution.isLoading = false
        state.execution.status = true
      })
      .addCase(resetPasswordThunk.rejected, (state) => {
        state.execution.isLoading = false
        state.execution.status = false
      })
      .addCase(setPasswordThunk.pending, (state) => {
        state.execution.isLoading = true
      })
      .addCase(setPasswordThunk.fulfilled, (state) => {
        state.execution.isLoading = false
        state.execution.status = true
      })
      .addCase(setPasswordThunk.rejected, (state) => {
        state.execution.isLoading = false
        state.execution.status = false
      })
  }
})

export const { resetAuthState, executionLoading, resetExecutionState, setSessionAnonymous } =
  authSlice.actions

export default authSlice.reducer
